import * as RadixContextMenu from '@radix-ui/react-context-menu';
import classNames from 'classnames';
import { memo, type ReactNode } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const contextMenuItem = tailwindVariants({
  base: 'flex min-h-[32px] shrink-0 cursor-pointer radix-disabled:cursor-not-allowed items-center rounded-4 radix-disabled:bg-transparent radix-highlighted:ads:bg-hover-subtle radix-highlighted:bg-gray-4 px-1.5 text-inherit radix-disabled:opacity-50 outline-none hover:ads:bg-hover-subtle hover:bg-gray-4',
  defaultVariants: {
    description: false,
  },
  variants: {
    description: {
      false: 'items-center py-0',
      true: 'items-start py-1.5',
    },
  },
});

export type ContextMenuItemVariant = VariantProps<typeof contextMenuItem>;

export type ContextMenuItemProps = Omit<RadixContextMenu.ContextMenuItemProps, 'prefix' | 'size'> &
  Omit<ContextMenuItemVariant, 'description'> & {
    description?: ReactNode;
    label?: ReactNode;
    prefix?: ReactNode;
    suffix?: ReactNode;
    shortcut?: string[];
  };

export const ContextMenuItem = memo(
  ({ children, className, description, label, prefix, shortcut, suffix, ...restOfProps }: ContextMenuItemProps) => {
    const hasDescription = !!description;

    return (
      <RadixContextMenu.Item
        className={contextMenuItem({ className, description: hasDescription })}
        data-testid="CONTEXT_MENU_ITEM"
        {...restOfProps}
      >
        {children ? (
          children
        ) : (
          <>
            {prefix ? (
              <div className={classNames('mr-2 flex shrink-0 items-center', hasDescription ? 'mt-0.5' : 'mt-0')}>
                {prefix}
              </div>
            ) : null}

            <div className="flex min-w-0 grow flex-col">
              <span className="truncate text-14">{label}</span>
              {hasDescription ? <span className="ads:text-metadata text-12 text-gray-10">{description}</span> : null}
            </div>

            {suffix ? (
              <div className={classNames('ml-2 flex shrink-0 items-center', hasDescription ? 'mt-0.5' : 'mt-0')}>
                {suffix}
              </div>
            ) : null}
            {shortcut ? (
              <div className="ml-2 flex shrink-0 items-center">
                {shortcut.map((key, index) => (
                  <kbd
                    className="mr-0.5 flex size-4 items-center justify-center rounded-4 ads:bg-subtle bg-gray-3 ads:text-metadata text-10 text-gray-8 uppercase last:mr-0"
                    key={index}
                  >
                    {key}
                  </kbd>
                ))}
              </div>
            ) : null}
          </>
        )}
      </RadixContextMenu.Item>
    );
  },
);

ContextMenuItem.displayName = 'ContextMenuItem';
